import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import './event-attribute-add-value-form.scss';
import { EventAttributeAddValueVM } from './event-attribute-add-value.viewmodel';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import { FormField } from '../../../../../global/form/form-field/form-field';
import DefaultButton from '../../../../../global/default-button/default-button';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import { EventAttributeAddValueFieldKey, FORM_CONFIG } from './event-attribute-add-value-form.config';
import { FormattedMessage, useIntl } from 'react-intl';
import Text from '../../../../../global/text/text';

export type EventAttributeAddValueProps = {
    vm: EventAttributeAddValueVM;
    positionMode?: 'edit' | 'add';
    positionStyle?: React.CSSProperties | null;
};

export const EventAttributeAddValuePresentation = observer(({ vm, positionMode = 'add', positionStyle = null }: EventAttributeAddValueProps): JSX.Element | null => {
    const intl = useIntl();
    const popupRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    const stopPropagation = (e: React.MouseEvent | React.FocusEvent) => {
        e.stopPropagation?.();
    };
    const stopPropagationOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation?.();
    };

    if (!vm.isOpen) return null;

    const { label, status, externalId } = vm.getFormFields(EventAttributeAddValueFieldKey);

    const statusTextConfig = status.value
        ? {
            id: 'oe.editValue.valueIsActive',
            message: 'Value is active',
            description: 'Status label when active'
        }
        : {
            id: 'oe.editValue.valueIsInactive',
            message: 'Value is inactive',
            description: 'Status label when inactive'
        };

        const overlayClassName = positionMode === 'edit'
        ? 'add-value-overlay add-value-overlay--edit-centered'
        : 'add-value-overlay';

    return (
        <div
            className={overlayClassName}
            ref={overlayRef}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            style={positionStyle || undefined}
        >
            <div
                className="add-value-popup"
                ref={popupRef}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
            >
                <CommonPopupBodyFooterLayout
                    body={
                        <div
                            className="oe-form oe-form--add-value"
                            onClick={stopPropagation}
                            onMouseDown={stopPropagation}
                        >
                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[EventAttributeAddValueFieldKey.LABEL].labelDescriptor} />}
                                className="m-bottom-15px"
                            >
                                <input
                                    className="oe-form-field__control"
                                    type="text"
                                    value={label.value as string}
                                    onChange={e => {
                                        stopPropagationOnChange(e);
                                        label.setValue(e.target.value);
                                        vm.clearLabelError();
                                    }}
                                    onClick={stopPropagation}
                                    onMouseDown={stopPropagation}
                                    onFocus={stopPropagation}
                                    onKeyDown={e => e.stopPropagation?.()}
                                    onBlur={label.setTouched}
                                />
                                <span className="oe-form-field__error">
                                    {vm.labelError && <span>{vm.labelError}</span>}
                                    {!label.valid && label.touched && (
                                        <FormattedMessage
                                            values={{
                                                fieldName: intl.formatMessage(FORM_CONFIG[EventAttributeAddValueFieldKey.LABEL].labelDescriptor),
                                            }}
                                            {...label.validationMessage}
                                        />
                                    )}
                                </span>
                            </FormField>

                            <FormField className="m-bottom-20px">
                                <div className="attribute-toggle">
                                    <OEToggle
                                        isSelected={status.value as boolean}
                                        onClick={vm.toggleStatus}
                                    />
                                    <Text
                                        id={statusTextConfig.id}
                                        message={statusTextConfig.message}
                                        description={statusTextConfig.description}
                                    />
                                </div>
                            </FormField>

                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[EventAttributeAddValueFieldKey.EXTERNAL_ID].labelDescriptor} />}
                            >
                                <input
                                    className="oe-form-field__control"
                                    type="text"
                                    value={externalId.value as string}
                                    onChange={e => {
                                        stopPropagationOnChange(e);
                                        externalId.setValue(e.target.value);
                                        vm.clearExternalIdError();
                                    }}
                                    onClick={stopPropagation}
                                    onMouseDown={stopPropagation}
                                    onFocus={stopPropagation}
                                    onKeyDown={e => e.stopPropagation?.()}
                                    onBlur={externalId.setTouched}
                                />
                                <span className="oe-form-field__error">
                                    {vm.externalIdError && <span>{vm.externalIdError}</span>}
                                    {!externalId.valid && externalId.touched && (
                                        <FormattedMessage
                                            values={{
                                                fieldName: intl.formatMessage(FORM_CONFIG[EventAttributeAddValueFieldKey.EXTERNAL_ID].labelDescriptor),
                                            }}
                                            {...externalId.validationMessage}
                                        />
                                    )}
                                </span>
                            </FormField>
                        </div>
                    }
                    footer={
                        <div className="buttons-block buttons-block--from-right">
                            <DefaultButton
                                text="Save"
                                modifier="confirm-safe"
                                onClick={e => {
                                    e?.stopPropagation?.();
                                    vm.saveValue(vm.attributeValues, intl);
                                }}
                                disabled={vm.isSaving}
                            />
                            <DefaultButton
                                text="Cancel"
                                modifier="cancel"
                                onClick={e => {
                                    e?.stopPropagation?.();
                                    vm.close();
                                }}
                            />
                        </div>
                    }
                />
            </div>
        </div>
    );
});
